import { get, isNil, round } from 'lodash';
import rewardTypes from '@enums/reward-types';
import requirementTypes from '@enums/requirement-types';
import { getTierPrimaryReward, getTierRequirementAmount } from './mechanic-reader';

const toPrice = value =>
    isNil(value) || Number.isNaN(value) ? null : round(Math.max(0, value), 2);

const isPositive = value => !isNil(value) && value > 0;

export const getRegularPrice = product => {
    const price = get(product, 'nonPromoPrices.avgPrice');
    return isPositive(price) ? price : null;
};

export const getStoredTierPromoPrice = (product, tierIndex) => {
    const price = get(product, ['promoPrices', tierIndex, 'avgPrice']);
    return isPositive(price) ? price : null;
};

// Per-unit promo price per reward type, used before the forecast has refreshed promoPrices.
const estimators = {
    [rewardTypes.newPricePerItem]: ({ amount }) => amount,
    [rewardTypes.newShelfPricePerItem]: ({ amount }) => amount,
    [rewardTypes.setMoney]: ({ amount, buy }) => amount / (buy || 1),
    [rewardTypes.savePercent]: ({ amount, regularPrice, nthItem }) =>
        isNil(regularPrice) ? null : regularPrice * (1 - amount / 100 / (nthItem || 1)),
    [rewardTypes.saveMoney]: ({ amount, regularPrice, buy }) =>
        isNil(regularPrice) ? null : regularPrice - amount / (buy || 1),
    [rewardTypes.saveMoneyPerItem]: ({ amount, regularPrice }) =>
        isNil(regularPrice) ? null : regularPrice - amount,
    [rewardTypes.getNFree]: ({ amount, regularPrice, buy }) =>
        isNil(regularPrice) || !buy ? null : (regularPrice * buy) / (buy + amount),
};

export function estimateTierPromoPrice({ offerMechanic, tierIndex, regularPrice }) {
    const reward = getTierPrimaryReward(offerMechanic, tierIndex);
    if (!reward || isNil(reward.amount)) return toPrice(regularPrice);

    const estimator = estimators[reward.type];
    if (!estimator) return toPrice(regularPrice);

    return toPrice(
        estimator({
            amount: reward.amount,
            regularPrice,
            buy: getTierRequirementAmount(offerMechanic, tierIndex, requirementTypes.buyNumber),
            nthItem: getTierRequirementAmount(offerMechanic, tierIndex, requirementTypes.onNthItem),
        })
    );
}

export function getTierPromoPrice({ offerMechanic, tierIndex, product, useStoredPrices }) {
    const stored = useStoredPrices ? getStoredTierPromoPrice(product, tierIndex) : null;
    if (!isNil(stored)) return stored;
    return estimateTierPromoPrice({
        offerMechanic,
        tierIndex,
        regularPrice: getRegularPrice(product),
    });
}

export const getUnitPrice = ({ price, packSize }) =>
    isNil(price) || !isPositive(packSize) ? null : toPrice(price / packSize);

export default {
    getRegularPrice,
    getStoredTierPromoPrice,
    estimateTierPromoPrice,
    getTierPromoPrice,
    getUnitPrice,
};
